import { type ReactNode, useMemo, useEffect } from 'react'
import { createFileRoute, Navigate, Outlet } from '@tanstack/react-router'
import { useLocation } from '@tanstack/react-router'
import SettingSideBarNav from '@/pages/settings/components/SettingSidebar'
import { ScrollArea } from '@deepguide-ai/ui/scroll-area'
import { Skeleton } from '@deepguide-ai/ui/skeleton'
import { useDocumentTitle } from 'usehooks-ts'
import { APP_NAME, EMAIL_INTERFACE_ENABLED } from '@/lib/env'
import { trackEvent } from '@/lib/posthog'
import { useFeatureFlag } from '@/hooks/useFeatureFlag'
import { useSubscription } from '@/hooks/useSubscription'
import { useWorkspacePermissions } from '@/hooks/useWorkspacePermissions'
import { useWorkspaceType } from '@/hooks/useWorkspaceType'
import { useActiveOrganization } from '@/hooks/useActiveOrganization'
import { useOrganizationPermissions } from '@/hooks/useOrganizationPermissions'
import MainHeader from '@/components/layout/MainHeader'
import { Main } from '@/components/main'
import { RequireAdmin } from '@/components/permissions'

export const Route = createFileRoute(
  '/(protected)/workspaces/$workspaceId/settings'
)({
  component: SettingsLayout,
})

// Routes that don't require admin access.
const PUBLIC_SETTINGS_ROUTES = [
  '/settings/profile',
  '/settings/members',
  '/settings/security',
  '/settings/browser-extension',
  '/settings/email',
  '/settings/exceptions',
]
const PUBLIC_SETTINGS_TITLES = new Set([
  'Profile',
  'Security',
  'Members',
  'Browser Extension',
  'Email Vera',
  'Exceptions',
])

// Temporary release flag: keep the route available for direct links while
// hiding the unfinished Request Intake entry from Settings navigation.
const REQUEST_INTAKE_SETTINGS_ENABLED = false

function SettingsLoading() {
  return (
    <div className='mx-auto w-full max-w-3xl space-y-4'>
      <Skeleton className='h-8 w-48' />
      <Skeleton className='h-24 w-full' />
      <Skeleton className='h-24 w-full' />
    </div>
  )
}

function SettingsAccessDenied() {
  return (
    <div className='mx-auto flex min-h-[320px] w-full max-w-2xl flex-col justify-center'>
      <h2 className='text-foreground text-[18px] font-semibold'>
        You do not have access to this settings page.
      </h2>
      <p className='text-muted-foreground mt-2 text-[13px]'>
        This page is only available to workspace admins.
      </p>
    </div>
  )
}

export function SettingsLayout() {
  useDocumentTitle(`Settings - ${APP_NAME}`)
  const { type } = useWorkspaceType()
  const location = useLocation()
  const { workspaceId } = Route.useParams()
  const {
    enabled: exceptionsManagementEnabled,
    loading: exceptionsManagementLoading,
  } = useFeatureFlag('exceptionsManagement')
  const {
    enabled: workspaceDataExportEnabled,
    loading: workspaceDataExportLoading,
  } = useFeatureFlag('workspaceDataExport')
  const { enabled: vantaImportEnabled, loading: vantaImportLoading } =
    useFeatureFlag('vantaImport')
  const { enabled: ssoEnabled, loading: ssoLoading } = useFeatureFlag('sso')
  const { isAdmin, isLoading: isLoadingPermissions } =
    useWorkspacePermissions(workspaceId)
  const { organization } = useActiveOrganization()
  const { isAdmin: isOrganizationAdmin } = useOrganizationPermissions(
    organization?.id
  )
  const { subscription } = useSubscription()
  const isEnterprise = subscription?.plan === 'ENTERPRISE'

  // Check if current route is a public settings page (doesn't require admin)
  const isPublicSettingsRoute = PUBLIC_SETTINGS_ROUTES.some((route) =>
    location.pathname.includes(route)
  )

  // Filter sidebar items based on workspace type, plan, and user role
  const filteredSidebarItems = useMemo(() => {
    let items = [...sidebarNavItems]

    if (!exceptionsManagementEnabled) {
      items = items.filter((item) => item.title !== 'Exceptions')
    }
    if (!vantaImportEnabled) {
      items = items.filter((item) => item.title !== 'Imports')
    }
    if (!workspaceDataExportEnabled) {
      items = items.filter((item) => item.title !== 'Data Export')
    }
    if (!ssoEnabled) {
      items = items.filter((item) => item.title !== 'Authentication & SSO')
    }
    if (!EMAIL_INTERFACE_ENABLED) {
      items = items.filter((item) => item.title !== 'Email Vera')
    }

    // Non-admin users see account-level settings plus the member list.
    if (!isAdmin) {
      items = items.filter((item) => PUBLIC_SETTINGS_TITLES.has(item.title))
      if (type !== 'COMPLIANCE' && type !== 'SCREENATA') {
        items = items.filter((item) => item.title !== 'Exceptions')
      }
      return items
    }

    if (!isOrganizationAdmin) {
      items = items.filter((item) => item.title !== 'Authentication & SSO')
    }

    // Filter by workspace type (admin only sees these)
    const isComplianceWorkspace = type === 'COMPLIANCE' || type === 'SCREENATA'
    if (isComplianceWorkspace) {
      // Hide Writing, Properties, and MCP Server for compliance workspaces.
      // (Framework enrollment / control matrix moved to Audits and Program
      // in #613 — the Settings › Compliance page was retired.)
      items = items.filter(
        (item) =>
          !['Writing', 'Properties', 'MCP Server', 'Billing'].includes(
            item.title
          )
      )
    } else {
      // Program, Compliance Roles, Governance, Exceptions, and Triggers (RFC-167 §13 SOP
      // event triggers) only apply to compliance (SCREENATA) workspaces.
      items = items.filter(
        (item) =>
          ![
            'Program',
            'Compliance Roles',
            'Governance',
            'Exceptions',
            'Triggers',
          ].includes(item.title)
      )
    }

    // Filter by plan/feature flags
    if (!REQUEST_INTAKE_SETTINGS_ENABLED) {
      items = items.filter((item) => item.title !== 'Request Intake')
    }

    // Signing settings only for Enterprise plan (RFC-132 BYOK)
    if (!isEnterprise) {
      items = items.filter((item) => item.title !== 'Signing')
    }

    return items
  }, [
    type,
    exceptionsManagementEnabled,
    workspaceDataExportEnabled,
    vantaImportEnabled,
    ssoEnabled,
    isAdmin,
    isOrganizationAdmin,
    isEnterprise,
  ])

  // Track settings page views
  useEffect(() => {
    // Extract page name from pathname (e.g., /workspaces/123/settings/members -> members)
    const pathParts = location.pathname.split('/')
    const settingsIndex = pathParts.indexOf('settings')
    const pageName = pathParts[settingsIndex + 1] || 'general'

    // Map page names to valid enum values
    const validPages = [
      'general',
      'properties',
      'compliance',
      'integrations',
      'writing',
      'templates',
      'mcp-server',
      'members',
      'api-keys',
      'personal-tokens',
      'billing',
      'profile',
      'signing',
      'notifications',
      'exceptions',
      'data-export',
    ]
    const page = validPages.includes(pageName) ? pageName : 'general'

    trackEvent('settings.page.viewed', {
      page,
      workspace_id: workspaceId,
    })
  }, [location.pathname, workspaceId])

  // Extract current settings page for audit logging
  const currentSettingsPage = useMemo(() => {
    const pathParts = location.pathname.split('/')
    const settingsIndex = pathParts.indexOf('settings')
    return pathParts[settingsIndex + 1] || 'general'
  }, [location.pathname])

  const renderSettingsContent = (content: ReactNode) => (
    <Main fixed className='flex h-screen flex-col'>
      <MainHeader className='!mb-0 !h-[50px] px-5'>
        <h1 className='font-heading text-foreground text-[15px] font-semibold'>
          Settings
        </h1>
      </MainHeader>
      <div className='flex h-[calc(var(--app-vh)_-_50px)]'>
        <aside className='border-border bg-surface-subtle w-[220px] shrink-0 border-r px-3 py-4'>
          <SettingSideBarNav items={filteredSidebarItems} />
        </aside>
        <ScrollArea className='h-full flex-1 p-6'>{content}</ScrollArea>
      </div>
    </Main>
  )

  if (location.pathname.includes('/settings/exceptions')) {
    if (exceptionsManagementLoading) {
      return renderSettingsContent(<SettingsLoading />)
    }
    if (!exceptionsManagementEnabled) {
      return (
        <Navigate
          to='/workspaces/$workspaceId/settings/general'
          params={{ workspaceId }}
          replace
        />
      )
    }
  }

  if (location.pathname.includes('/settings/data-export')) {
    if (workspaceDataExportLoading) {
      return renderSettingsContent(<SettingsLoading />)
    }
    if (!workspaceDataExportEnabled) {
      return (
        <Navigate
          to='/workspaces/$workspaceId/settings/general'
          params={{ workspaceId }}
          replace
        />
      )
    }
  }

  if (location.pathname.includes('/settings/imports')) {
    if (vantaImportLoading) {
      return renderSettingsContent(<SettingsLoading />)
    }
    if (!vantaImportEnabled) {
      return (
        <Navigate
          to='/workspaces/$workspaceId/settings/general'
          params={{ workspaceId }}
          replace
        />
      )
    }
  }

  if (location.pathname.includes('/settings/auth')) {
    if (ssoLoading) {
      return renderSettingsContent(<SettingsLoading />)
    }
    if (!ssoEnabled) {
      return (
        <Navigate
          to='/workspaces/$workspaceId/settings/general'
          params={{ workspaceId }}
          replace
        />
      )
    }
  }

  // Public settings routes (profile, members) are accessible to all users
  if (isPublicSettingsRoute) {
    return renderSettingsContent(<Outlet />)
  }

  // Do not mount admin-only settings children until the role is known.
  if (isLoadingPermissions) {
    return renderSettingsContent(<SettingsLoading />)
  }

  // Admin-only routes require admin access - non-admins will see 403
  return (
    <RequireAdmin
      resource={`settings/${currentSettingsPage}`}
      fallback={renderSettingsContent(<SettingsAccessDenied />)}
    >
      {renderSettingsContent(<Outlet />)}
    </RequireAdmin>
  )
}

const sidebarNavItems = [
  {
    title: 'General',
    href: '/settings/general',
    label: 'Organization',
  },
  {
    title: 'Program',
    href: '/settings/program',
    label: 'Organization',
  },
  {
    title: 'Members',
    href: '/settings/members',
    label: 'Organization',
  },
  {
    title: 'Compliance Roles',
    href: '/settings/compliance-roles',
    label: 'Organization',
  },
  {
    title: 'Governance',
    href: '/settings/governance',
    label: 'Organization',
  },
  {
    title: 'Properties',
    href: '/settings/properties',
    label: 'Organization',
  },
  // {
  //   title: 'Integrations',
  //   href: '/settings/integrations',
  //   label: 'Organization',
  // },
  {
    title: 'Writing',
    href: '/settings/writing',
    label: 'Organization',
  },
  {
    title: 'MCP Server',
    href: '/settings/mcp-server',
    label: 'Organization',
  },
  {
    // RFC-206 §4.5 — signed inbound webhooks for external request intake.
    title: 'Request Intake',
    href: '/settings/request-intake',
    label: 'Organization',
  },
  // 'API Keys' is intentionally hidden while self-service Enterprise API key
  // provisioning is not exposed. The gated route remains reachable by direct URL.
  {
    title: 'Personal Tokens',
    href: '/settings/personal-tokens',
    label: 'Account',
  },
  {
    title: 'Billing',
    href: '/settings/billing',
    label: 'Organization',
  },
  {
    title: 'Audit Logs',
    href: '/settings/audit-logs',
    label: 'Organization',
  },
  { title: 'Imports', href: '/settings/imports', label: 'Organization' },
  {
    title: 'Data Export',
    href: '/settings/data-export',
    label: 'Organization',
  },
  {
    title: 'Authentication & SSO',
    href: '/settings/auth',
    label: 'Organization',
  },
  {
    title: 'Exceptions',
    href: '/settings/exceptions',
    label: 'Organization',
  },
  // Procedure Triggers moved to Procedures → Triggers tab (one home). The
  // /settings/procedure-triggers route still resolves for deep links.
  // {
  //   title: 'Notifications',
  //   href: '/settings/notifications',
  //   label: 'Organization',
  // },
  {
    title: 'Signing',
    href: '/settings/signing',
    label: 'Organization',
  },
  {
    title: 'Email Vera',
    href: '/settings/email',
    label: 'Account',
  },
  {
    title: 'Browser Extension',
    href: '/settings/browser-extension',
    label: 'Account',
  },
  {
    title: 'Profile',
    href: '/settings/profile',
    label: 'Account',
  },
  {
    title: 'Security',
    href: '/settings/security',
    label: 'Account',
  },
]
